/** Hardware zoom helpers shared by the photo capture flow and the QR scanner. */

export interface ZoomSupport {
  levels: number[];
  current: number;
}

/** Chip levels within the track's [min,max]: 1× plus 2×/3×/5× where available. */
export function zoomLevelsFor(min: number, max: number): number[] {
  const candidates = [1, 2, 3, 5];
  const levels = candidates.filter((level) => level >= min && level <= max);
  if (!levels.includes(min) && min < 1) levels.unshift(Math.round(min * 10) / 10); // ultrawide 0.5×
  return levels;
}

/**
 * Probe a video track for hardware zoom (Android Chrome, iOS 17+ Safari).
 * Returns null when unsupported or when only one usable level exists.
 * `zoom` is not in lib.dom's MediaTrackCapabilities yet — hence the cast.
 */
export function zoomSupportOf(track: MediaStreamTrack | null | undefined): ZoomSupport | null {
  const caps = track?.getCapabilities?.() as
    | (MediaTrackCapabilities & { zoom?: { min: number; max: number } })
    | undefined;
  if (!caps?.zoom || caps.zoom.max <= caps.zoom.min) return null;
  const levels = zoomLevelsFor(caps.zoom.min, caps.zoom.max);
  if (levels.length < 2) return null;
  return { levels, current: levels.includes(1) ? 1 : levels[0]! };
}

/** Apply a zoom level to a live track (best-effort; resolves to success). */
export async function applyZoom(track: MediaStreamTrack, level: number): Promise<boolean> {
  try {
    await track.applyConstraints({ advanced: [{ zoom: level } as MediaTrackConstraintSet] });
    return true;
  } catch {
    return false;
  }
}
